import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ROLES } from '../constants';
import * as filesApi from '../services/filesApi';

const SERVICE_BASE_URL = process.env.REACT_APP_SERVICE_BASE_URL || '';
function buildUrl(url) {
  if (SERVICE_BASE_URL && url.startsWith('/')) return `${SERVICE_BASE_URL}${url}`;
  return url;
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Host-configurable upload rules, resolved server-side for the active organisation. Held here
  // rather than in the bundle so a settings change takes effect without a redeployment.
  const [uploadPolicy, setUploadPolicy] = useState(null);

  const fetchUser = useCallback(async () => {
    try {
      const res = await fetch(buildUrl('/api/Account/me'), { credentials: 'include' });
      if (res.ok) {
        setUser(await res.json());
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  const login = async (email, password) => {
    const res = await fetch(buildUrl('/api/Account/login'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    if (res.ok) {
      const data = await res.json();
      setUser(data);
      return { success: true, requiresOrgSelection: data.requiresOrgSelection === true };
    }
    if (res.status === 403) {
      const body = await res.json().catch(() => null);
      return { success: false, error: body?.error || 'Your organisation has been suspended.' };
    }
    return { success: false, error: 'Invalid email or password.' };
  };

  const register = async (email, password, firstName, lastName, organisationName) => {
    const res = await fetch(buildUrl('/api/Account/register'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, firstName, lastName, organisationName }),
    });
    if (res.status === 202) {
      const body = await res.json().catch(() => null);
      return { success: false, pending: true, message: body?.message || 'Your organisation is awaiting approval.' };
    }
    if (res.ok) {
      const data = await res.json();
      setUser(data);
      return { success: true };
    }
    const body = await res.json().catch(() => null);
    const errors = body?.errors
      ? Object.values(body.errors).flat().join(' ')
      : 'Registration failed.';
    return { success: false, error: errors };
  };

  // organisationId null = switch to the host (platform) context.
  const switchOrg = async (organisationId = null) => {
    const res = await fetch(buildUrl('/api/Account/switch-org'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ organisationId: organisationId ?? null }),
    });
    if (res.ok) {
      const data = await res.json();
      setUser(data);
      return { success: true };
    }
    const body = await res.json().catch(() => null);
    return { success: false, error: body?.error || 'Failed to switch organisation.' };
  };

  const logout = async () => {
    await fetch(buildUrl('/api/Account/logout'), { method: 'POST', credentials: 'include' });
    setUser(null);
  };

  const canEdit = !!user && (user.roles?.includes(ROLES.ADMINISTRATOR) || user.roles?.includes(ROLES.MEMBER));
  const isAdmin = !!user && user.roles?.includes(ROLES.ADMINISTRATOR);
  const requiresOrgSelection = !!user && user.requiresOrgSelection === true;
  const isAuthenticated = !!user && !requiresOrgSelection;

  // Also called after the host edits upload settings, so the screens showing the effective values
  // do not keep displaying what applied a moment ago.
  const refreshUploadPolicy = useCallback(async () => {
    try {
      setUploadPolicy(await filesApi.getUploadPolicy());
    } catch {
      setUploadPolicy(null);
    }
  }, []);

  // Refetched when the active organisation changes, since an org may carry its own override.
  useEffect(() => {
    if (!isAuthenticated) {
      setUploadPolicy(null);
      return undefined;
    }
    let cancelled = false;
    filesApi.getUploadPolicy()
      .then((policy) => { if (!cancelled) setUploadPolicy(policy); })
      .catch(() => { if (!cancelled) setUploadPolicy(null); });
    return () => { cancelled = true; };
  }, [isAuthenticated, user?.organisationId]);

  // Permission-based gating (RBAC) — preferred over role checks for new code.
  const hasPermission = useCallback(
    (permission) => !!user?.permissions?.includes(permission),
    [user],
  );

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      uploadPolicy,
      refreshUploadPolicy,
      login,
      register,
      logout,
      switchOrg,
      refreshUser: fetchUser,
      isAuthenticated,
      requiresOrgSelection,
      canEdit,
      isAdmin,
      hasPermission,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
